// One SCOUT hunt's ATTRITION LADDER, rendered as a sentence.
//
// `0 ranked of 0 found` was the whole story a search card told, and it reads as an
// empty marketplace no matter what actually happened. On 2026-08-13 a preprod
// concierge hunt pooled 0 out of 257 sellers the gateway returned IN FULL —
// Federation's gig sub-resolver nulled every package for ~12 minutes, every call was
// 200 OK, so `degraded` stayed false. The console blamed the marketplace and the
// client was told to refine a brief that was fine; the same brief ranked 18 talents
// 12 minutes later.
//
// The backend now persists where the sellers went (`scout_searches.funnel`, migration
// 0152). This turns that into the line an operator reads.
//
// EVERY rung is a count of SELLERS, so the line subtracts cleanly end to end. That is a
// backend contract (`scout.discovery._funnel_counts`), not something this file can
// enforce — but it is why an unfamiliar key is dropped rather than guessed at: a rung
// in another unit reads as arithmetic that does not close.
//
// Pure and string-only on purpose: the admin app's tests are logic tests with no DOM
// (see alert-cell.test.ts), so the formatting is testable without rendering.

/** Operator-facing name per seam. A key missing here is a key we do not render:
 *  the backend vocabulary can grow without this file guessing at a label. */
const LABELS: Readonly<Record<string, string>> = {
  seen: "seen",
  no_gig_data: "missing gig data",
  level_dropped: "below seller level",
  filter_dropped: "cut by filters",
  unaffordable: "over budget",
  over_grade_cap: "past the grading cap",
  grade_skipped: "not reached",
  graded: "graded",
  pooled: "pooled",
  ranked: "ranked",
};

/** Pipeline order, not object order: a ladder only reads as a ladder in sequence. */
const ORDER = [
  "seen",
  "no_gig_data",
  "level_dropped",
  "filter_dropped",
  "unaffordable",
  "over_grade_cap",
  "grade_skipped",
  "graded",
  "pooled",
  "ranked",
] as const;

const FILTER_PREFIX = "filter:";

/** The ladder as one line, or null when there is nothing to say.
 *
 * Null (not an empty string) for a row with no ladder — rows written before
 * migration 0152 carry `{}`, and the caller renders nothing rather than an empty
 * paragraph that still takes vertical space. */
export function funnelLadderText(funnel: Record<string, number> | null | undefined): string | null {
  if (!funnel) return null;
  const steps = ORDER.filter((k) => typeof funnel[k] === "number").map(
    (k) => `${funnel[k].toLocaleString()} ${LABELS[k]}`,
  );
  if (steps.length === 0) return null;
  // The per-filter breakdown SUMS to `filter_dropped`, so it rides in parentheses
  // rather than as more rungs — listing it flat would read as extra losses.
  const byFilter = Object.keys(funnel)
    .filter((k) => k.startsWith(FILTER_PREFIX) && funnel[k] > 0)
    .sort((a, b) => funnel[b] - funnel[a])
    .map((k) => `${k.slice(FILTER_PREFIX.length)} ${funnel[k].toLocaleString()}`);
  return steps.join(" → ") + (byFilter.length > 0 ? ` (${byFilter.join(", ")})` : "");
}

/** Sellers the upstream returned without a priced package, when there were any.
 *
 * Its own accessor because it earns its own badge: it is the one seam an operator
 * cannot infer from the other numbers, and the one that produced a 0-pool hunt on a
 * perfectly good brief. */
export function missingGigData(funnel: Record<string, number> | null | undefined): number {
  const n = funnel?.no_gig_data;
  return typeof n === "number" && n > 0 ? n : 0;
}
